import React, { useRef } from "react";

import { Box, Stack, Typography } from "@mui/material";

import { DigitInput } from "./styles";

const DEFAULT_LENGTH = 6;

export interface CodeDigitInputProps {
  value: string;
  onChange: (code: string) => void;
  length?: number;
  label?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  "data-cy"?: string;
  "aria-label"?: string;
}

const CodeDigitInput: React.FC<CodeDigitInputProps> = ({
  value,
  onChange,
  length = DEFAULT_LENGTH,
  label,
  required,
  disabled,
  error,
  errorMessage,
  "data-cy": dataCy,
  "aria-label": ariaLabel,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleDigitChange = (index: number, digitValue: string) => {
    if (digitValue.length > 1) return;
    const digit = digitValue.replace(/\D/g, "");
    const newCode = value.slice(0, index) + digit + value.slice(index + 1);
    onChange(newCode.slice(0, length));
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (pasted.length > 0) {
      onChange(pasted);
      const nextIndex = Math.min(pasted.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <Box width="100%">
      {label != null && (
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }} component="label">
          {label}
          {required && " *"}
        </Typography>
      )}
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: "nowrap", width: "100%" }}>
        {Array.from({ length }).map((_, index) => (
          <DigitInput
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            aria-label={ariaLabel ? `${ariaLabel} ${index + 1}` : `Digit ${index + 1}`}
            value={value[index] ?? ""}
            disabled={disabled}
            className={error ? "error" : ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleDigitChange(index, e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              handleDigitKeyDown(index, e)
            }
            onPaste={index === 0 ? handlePaste : undefined}
            data-cy={dataCy ? `${dataCy}${index}` : undefined}
          />
        ))}
      </Stack>
      {errorMessage && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default CodeDigitInput;
